interface ResultType {
    booking: Booking | undefined;
    productInfos: ProductInfo[];
    ticketGroups: TicketGroup[];
    refundableGroups: RefundableGroup[];
    exchangeableGroups: ExchangeableGroup[];
}

type ApiResponseType = ApiSchema['BookingResponse'];
type ParamsType = object;

/**
 * Loads a booking by its id and transforms it for the eSAV view.
 *
 * Based on the type of booking, the result will re-create all its details
 * (passengers, products with offers, ticket downloads, refundables).
 */
export default async function apiLoadEsavBooking(id: string): Promise<ApiResult<ResultType>> {
    try {
        return await callApiGet<ResultType, ApiResponseType, ParamsType>(
            `/bookings/${id}`,

            // Request Params
            {},

            // Transform the result
            (response: ApiResponseType) => {
                // Get the transformed booking object
                const booking = transformBooking(response);
                // Flatten all booking parts (and remove exchanged)
                const bookingParts = flattenBookingParts(response);
                // Transform ticket groups (for voucher downloads)
                const ticketGroups = transformTicketGroups(bookingParts, response);
                // Transform refundable groups (logical group of refundable fulfillments)
                const refundableGroups = transformRefundableGroups(bookingParts, response);
                // Transform exchangeable groups (logical group of exchangeable fulfillments, e.g. seat reservations)
                const exchangeableGroups = transformExchangeableGroups(bookingParts, response);
                // Transform product infos (landing page info for products)
                const productInfos = transformProductInfos(bookingParts, response, exchangeableGroups);

                return {
                    booking,
                    productInfos,
                    ticketGroups,
                    refundableGroups,
                    exchangeableGroups,
                } satisfies ResultType;
            },
        );
    }
    catch (error: any) {
        if (error.status === 404 || error.status === 400) {
            throw createError({
                fatal: true,
                status: 404,
                message: 'Booking not found',
            });
        }
        throw createError({
            fatal: true,
            status: 500,
            message: error.message,
        });
    }
}
